/* eslint-disable @atlassian/relay/unused-fields */

import { useContext } from 'react';

import { graphql, fetchQuery, ReactRelayContext } from 'react-relay';

import { createAppAri } from '@atlassian/cs-ari';

import { useTracingContext } from '../../../error-reporting';
import { createAtlAttributionHeader } from '../../../utils';
import {
	type useForgeAppQuery_appByIdQuery as appQuery,
	type useForgeAppQuery_appByIdQuery$data as appQueryData,
} from './__generated__/useForgeAppQuery_appByIdQuery.graphql';

export type AppData = NonNullable<appQueryData['app']>;

export class ForgeAppQueryError extends Error {}

export function useForgeAppQuery(): (appId: string, contextIds: string[]) => Promise<AppData> {
	const tracing = useTracingContext();
	const relayContext = useContext(ReactRelayContext);
	const environment = relayContext?.environment;

	const fetchAppData = async (appId: string, contextIds: string[]): Promise<AppData> => {
		const appAri = createAppAri({ appId }).toString();
		tracing?.recordGqlCall('APP_BY_ID_QUERY - start');

		// TODO move query fragment into AdminConsentModal.tsx
		return new Promise((resolve, reject) => {
			if (environment) {
				fetchQuery<appQuery>(
					environment,
					graphql`
						query useForgeAppQuery_appByIdQuery($appAri: ID!, $contextIds: [ID!]!) {
							app(id: $appAri) @required(action: THROW) {
								name
								privacyPolicy
								termsOfService
								installationsByContexts(contextIds: $contextIds) {
									edges {
										node {
											appEnvironmentVersion {
												extensions {
													nodes {
														extensionData
													}
												}
											}
										}
									}
								}
							}
						}
					`,
					{
						appAri,
						contextIds,
					},
					{
						networkCacheConfig: {
							metadata: {
								headers: {
									'atl-attribution': createAtlAttributionHeader(),
								},
							},
						},
					},
				).subscribe({
					next: (data) => {
						resolve(data.app);
					},
					error: (error: Error) => {
						const details = JSON.stringify(error);
						reject(
							new ForgeAppQueryError(
								`Failed to fetch app data by its ID: ${appAri}, GraphQLError: ${details}`,
							),
						);
					},
					complete: () => {
						tracing?.recordGqlCall('APP_BY_ID_QUERY - end');
					},
				});
			}
		});
	};

	return fetchAppData;
}
